import { ColDef } from "ag-grid-community";
import { buildColumnDefs } from "@platform/design-system";

import { formatDate } from "../../utils";
import {
  InfoHeaderIcon,
  renderCell,
  RenderCellLink,
} from "../../components/ClientCells";
import { formatCurrency } from "../../utils";

export interface Client {
  id: number;
  externalId: string;
  brokerId: number;
  renewalDate: string;
  name: string;
  numberOfEmployees: number;
  type: string;
  additionalInfo: {
    practice: string | null;
    officeLocation: string | null;
    clientExecutive: string | null;
    corporateEntity: string | null;
    onedigitalOffice: string | null;
  };
  sic: string;
  clientSince: string; // Consider `Date` if parsed, but string is fine if raw
  producer: string;
  createdAt: string; // ISO string
  updatedAt: string; // ISO string
  deletedAt: string | null;
  situsState: string;
  producerEmail: string;
  producerLastName: string;
  producerFirstName: string;
  sfExternalId: string | null;
  brokerName: string;
  soonestRenewalDate: string; // ISO string
  strategicCarriers: string[];
  strategicPremium: number;
  nonStrategicCarriers: string[];
  nonStrategicPremium: number;
  coverages: number;
  totalPremium: number;
  products: string[];
}

const BASE_COLUMNS = [
  "name",
  "renewalDate",
  "strategicCarriers",
  "strategicPremium",
  "nonStrategicCarriers",
  "nonStrategicPremium",
  "totalPremium",
] as const;

const ADMIN_COLUMNS = [
  "name",
  "brokerName",
  "renewalDate",
  "strategicPremium",
  "nonStrategicPremium",
  "totalPremium",
] as const;

const ALL_COLUMNS: Record<string, ColDef> = {
  name: {
    field: "name",
    headerName: "Client Name",
    sortable: true,
    cellRenderer: (params) => RenderCellLink(params),
    sort: "asc",
  },
  brokerName: {
    field: "brokerName",
    headerName: "Registered Broker",
    sortable: true,
    cellRenderer: (params) => renderCell(params.value),
  },
  renewalDate: {
    field: "renewalDate",
    headerName: "Renewal Date",
    sortable: true,
    headerTooltip: "This date is based on the earliest policy renewal date",
    cellRenderer: (params) => renderCell(params.value),
    headerComponent: "agColumnHeader",
    headerComponentParams: {
      innerHeaderComponent: InfoHeaderIcon,
      infoIcon: true,
    },
  },
  strategicCarriers: {
    field: "strategicCarriers",
    headerName: "Strategic Carriers",
    sortable: false,
    headerTooltip: "Your firm’s designated strategic carrier partners",
    cellRenderer: (params) => renderCell(params.value),
    headerComponent: "agColumnHeader",
    headerComponentParams: {
      innerHeaderComponent: InfoHeaderIcon,
    },
  },
  strategicPremium: {
    field: "strategicPremium",
    headerName: "Strategic Carriers Premium",
    sortable: true,
    cellRenderer: (params) => renderCell(formatCurrency(params.value)),
  },
  nonStrategicCarriers: {
    field: "nonStrategicCarriers",
    headerName: "Non-Strategic Carriers",
    sortable: false,
    cellRenderer: (params) => renderCell(params.value.length),
    headerTooltip:
      "Carriers outside of your firm’s designated strategic carriers",
    headerComponent: "agColumnHeader",
    headerComponentParams: {
      innerHeaderComponent: InfoHeaderIcon,
    },
  },
  nonStrategicPremium: {
    field: "nonStrategicPremium",
    headerName: "Non-Strategic Carriers Premium",
    sortable: true,
    cellRenderer: (params) => renderCell(formatCurrency(params.value)),
  },
  totalPremium: {
    field: "totalPremium",
    headerName: "Total Premium",
    sortable: true,
    cellRenderer: (params) => renderCell(formatCurrency(params.value)),
  },
};

export const headers = buildColumnDefs(ALL_COLUMNS, BASE_COLUMNS);
export const headersAdmin = buildColumnDefs(ALL_COLUMNS, ADMIN_COLUMNS);

export const formatResponse = (clients: Client[]): Client[] =>
  clients.map((client) => ({
    ...client,
    renewalDate: formatDate(client.soonestRenewalDate, true) as string,
    strategicPremium: client.strategicPremium,
    nonStrategicPremium: client.nonStrategicPremium,
    totalPremium: client.totalPremium,
  }));

export function getClientQueryKey(
  id: string | number,
): [string, string | number] {
  return ["clients", id];
}
